import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {ConversationComment} from '../../../page-data/payloads/conversation-comments'
import {ownerAvatarPath} from '@github-ui/paths'
import {getCurrentUserLogin} from '@github-ui/client-env'

export interface AuthorFilterItem {
  login: string
  avatarUrl: string
  count: number
  isAgent?: boolean
}

type CurrentUser = Pick<AuthorFilterItem, 'login' | 'avatarUrl' | 'isAgent'>

export const getCurrentUser = () => {
  const login = getCurrentUserLogin()
  if (login) return {login, avatarUrl: ownerAvatarPath({owner: login}), isAgent: false}
}

const compareAuthors = (currentUser: CurrentUser | undefined) => (a: AuthorFilterItem, b: AuthorFilterItem) => {
  if (a.login === currentUser?.login) return -1
  if (b.login === currentUser?.login) return 1
  if (a.login === 'Copilot') return -1
  if (b.login === 'Copilot') return 1
  return a.login.localeCompare(b.login)
}

/** The subset of author fields the filter list needs, shared by comment and thread authors. */
interface FilterableAuthor {
  login: string
  avatarUrl?: string | null
  isAgent?: boolean | null
}

/**
 * Builds a list of unique authors, sorted with the current user and Copilot at the top.
 *
 * Every author in `items` is included, but only `countItems` contribute to the counts, so callers
 * can keep the full set of selectable authors while showing counts for the filtered subset.
 */
function getAuthors<T>(
  items: readonly T[],
  countItems: readonly T[],
  getAuthor: (item: T) => FilterableAuthor | null | undefined,
): AuthorFilterItem[] {
  const currentUser = getCurrentUser()

  // Start with current user so they are always present in the list
  const authors = new Map<string, AuthorFilterItem>(
    currentUser ? [[currentUser.login, {...currentUser, count: 0}]] : [],
  )

  for (const item of items) {
    const author = getAuthor(item)
    if (!author) continue

    const existingAuthor = authors.get(author.login)
    if (existingAuthor) {
      existingAuthor.isAgent = existingAuthor.isAgent === true && author.isAgent === true
    } else {
      authors.set(author.login, {
        login: author.login,
        avatarUrl: author.avatarUrl ?? '',
        count: 0,
        isAgent: author.isAgent ?? false,
      })
    }
  }

  for (const item of countItems) {
    const author = getAuthor(item)
    if (!author) continue

    const entry = authors.get(author.login) ?? {
      login: author.login,
      avatarUrl: author.avatarUrl ?? '',
      count: 0,
      isAgent: author.isAgent ?? false,
    }
    entry.count++
    authors.set(author.login, entry)
  }

  return Array.from(authors.values()).sort(compareAuthors(currentUser))
}

/** Builds a list of unique authors from issue comments and review summaries. */
export function getConversationCommentsAuthors(
  comments: readonly ConversationComment[],
  countComments: readonly ConversationComment[] = comments,
): AuthorFilterItem[] {
  return getAuthors(comments, countComments, comment => comment.author ?? undefined)
}

/** Builds a list of unique authors from the first comment of each thread. */
export function getThreadsAuthors(
  threads: readonly Thread[],
  countThreads: readonly Thread[] = threads,
): AuthorFilterItem[] {
  return getAuthors(threads, countThreads, thread => getFirstComment(thread)?.author)
}
